<template>
  <!--
    Renders nothing until the answer is known, and nothing at all once a profile
    is claimed. Everything the flow needs to explain lives in the modal, so the
    page only ever carries one button.
  -->
  <div v-if="visible" :class="['claim', `claim--${variant}`]">
    <button
      v-if="!forced"
      type="button"
      class="claim-btn"
      @click="onTrigger"
    >
      <!--
        Always "Claim this profile", never "Finish claiming": nothing is stored
        before confirmation, so there is never a half-done claim to finish.
      -->
      <span class="claim-btn-ico" aria-hidden="true">🎮</span>
      {{ $t('claim.button') }}
    </button>

    <teleport to="body">
      <div
        v-if="effectiveOpen"
        class="claim-backdrop"
        role="presentation"
        @click.self="close"
      >
        <div
          class="claim-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="claim-modal-title"
        >
          <button
            v-if="canClose"
            type="button"
            class="claim-close"
            :aria-label="$t('claim.close')"
            @click="close"
          >✕</button>
          <!-- Forced mode: a brand-new unclaimed lookup opens this modal
               automatically. On the first lookup it cannot be dismissed until
               the visitor has at least sent the confirm mail (or hit a dead-end
               state); on a return visit it is closable (`dismissible`) but
               still needs to say why it is here, so this is keyed on `forced`
               rather than on `canClose`. -->
          <p v-if="forced && !existingProfile && !notRegisterable" class="claim-note claim-note--forced">
            {{ $t('claim.forcedIntro') }}
          </p>

          <!-- Step 1. The Steam round-trip is the only way to prove ownership,
               so it is stated plainly rather than dressed up as optional. -->
          <template v-if="stage === 'signed-out'">
            <p class="claim-step">{{ $t('claim.step1') }}</p>
            <h2 id="claim-modal-title" class="claim-heading">{{ $t('claim.heading') }}</h2>
            <!-- The deletion framing is written for the optional card; in
                 forced mode claim.forcedIntro above already explains why this
                 modal is here, and showing both is a mixed message. An
                 existing profile is not "new", so it gets this one instead. -->
            <p v-if="!forced || existingProfile" class="claim-body">
              {{ $t('claim.intro') }}
            </p>
            <ol class="claim-steps">
              <li>{{ $t('claim.stepSignIn') }}</li>
              <li>{{ $t('claim.stepConfirm') }}</li>
            </ol>
            <!--
              Valve's own asset rather than a styled button of ours. It is what
              Steam asks sign-in-through-Steam sites to use, it is what users
              recognise before handing over credentials, and the "not associated
              with Valve Corp." line they require is baked into the image.
              Served from public/ instead of hotlinked off steamstatic so the
              one button the whole flow depends on has no third-party request.
            -->
            <button type="button" class="claim-steam" @click="startLogin">
              <img
                src="/steam-signin.png"
                :alt="$t('claim.steamSignInAlt')"
                width="180"
                height="35"
              >
            </button>
            <p class="claim-note">
              {{ $t('claim.signInNote') }}
            </p>
          </template>

          <!-- Step 2. -->
          <template v-else-if="stage === 'need-email'">
            <p class="claim-step">{{ $t('claim.step2') }}</p>
            <!-- The library check answered as soon as we knew who the visitor
                 was (/claim/status). Showing the address field here and refusing
                 on submit is the same refusal one step too late — and so is any
                 copy that mentions an address: on 2026-10-10 an "Add an email…"
                 above a missing form had a visitor waiting for a mail. -->
            <template v-if="notRegisterable">
              <h2 id="claim-modal-title" class="claim-heading">{{ $t(playtimeHidden ? 'claim.playtimeHeading' : 'claim.privateHeading') }}</h2>
              <p class="claim-body">{{ $t(playtimeHidden ? 'claim.playtimePrivate' : 'claim.notPublic') }}</p>
            </template>
            <template v-else>
              <h2 id="claim-modal-title" class="claim-heading">{{ $t('claim.confirmHeading') }}</h2>
              <i18n-t scope="global" keypath="claim.signedInAs" tag="p" class="claim-body">
                <template #id><strong>{{ status.steamId }}</strong></template>
              </i18n-t>
            </template>
            <form v-if="!notRegisterable" class="claim-form" @submit.prevent="submitEmail">
              <input
                v-model="email"
                type="email"
                class="claim-input"
                :placeholder="$t('claim.emailPlaceholder')"
                autocomplete="email"
                :disabled="sending"
                :aria-label="$t('claim.emailAria')"
              >
              <button type="submit" class="claim-btn claim-btn--block" :disabled="sending || !email">
                {{ sending ? $t('claim.sending') : $t('claim.sendLink') }}
              </button>
            </form>
            <!-- Named up front rather than left for the error message to reveal:
                 only these providers are accepted, and guessing is a bad first
                 experience. -->
            <p v-if="!notRegisterable" class="claim-note">
              {{ $t('claim.providersNote') }}
            </p>
            <p v-if="error" class="claim-note claim-note--error">{{ error }}</p>
          </template>

          <!--
            Steam came back with a different account than the profile on screen.
            This is the only place the mismatch can honestly be reported: before
            the round-trip nobody knows who the visitor is.
          -->
          <template v-else-if="stage === 'mismatch'">
            <p class="claim-step">{{ $t('claim.wrongProfile') }}</p>
            <h2 id="claim-modal-title" class="claim-heading">{{ $t('claim.mismatchHeading') }}</h2>
            <i18n-t scope="global" keypath="claim.mismatchBody" tag="p" class="claim-body">
              <template #signedId><strong>{{ status.steamId }}</strong></template>
              <template #pageId><strong>{{ profileSteamId }}</strong></template>
            </i18n-t>
            <button type="button" class="claim-btn claim-btn--block" @click="goToOwnProfile">
              {{ $t('claim.goToMyProfile') }}
            </button>
          </template>

          <template v-else-if="stage === 'pending'">
            <p class="claim-step">{{ $t('claim.almostThere') }}</p>
            <h2 id="claim-modal-title" class="claim-heading">{{ $t('claim.checkInbox') }}</h2>
            <i18n-t scope="global" keypath="claim.sentBody" tag="p" class="claim-body">
              <template #email><strong>{{ sentTo }}</strong></template>
            </i18n-t>
            <button type="button" class="claim-btn claim-btn--block" :disabled="sending" @click="resend">
              {{ sending ? $t('claim.sending') : $t('claim.sendAgain') }}
            </button>
            <button type="button" class="claim-link" @click="sentTo = ''">
              {{ $t('claim.useDifferent') }}
            </button>
            <p v-if="error" class="claim-note claim-note--error">{{ error }}</p>
          </template>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { claimAPI } from '../../services/api';
import { trackEvent } from '../../utils/analytics';

export default {
    name: 'ClaimCard',
    // Provided by Dashboard: true while its forced instance is on screen.
    inject: {
        isClaimGateOpen: { default: () => () => false },
    },
    props: {
        // The profile currently on screen. Owner-only states are gated on this.
        profileSteamId: { type: String, default: '' },
        // The raw identifier the visitor typed. Differs from profileSteamId
        // exactly when they typed a vanity nobody has claimed — the case the
        // login stamp exists for.
        lookupKey: { type: String, default: '' },
        variant: {
            type: String,
            default: 'full',
            validator: v => ['full', 'compact', 'achievements'].includes(v),
        },
        // The mandatory claim gate: opens the modal without a click and
        // blocks dismissal until the visitor reaches a stage where waiting on
        // them further stops being useful (mail sent, already claimed, or a
        // mismatch they can't resolve here).
        forced: { type: Boolean, default: false },
        // Opens the gate but leaves it closable. Used on a RETURN visit
        // (see Dashboard's mandatoryClaimReason): measured 2026-08-19..09-02,
        // re-blocking someone who already refused once cost engagement
        // (returning visitors engaged 41% under the hard gate vs 67% without)
        // without adding conversions — every mail comes from the first lookup.
        dismissible: { type: Boolean, default: false },
        // Forced gate on a profile that already has data but was never claimed
        // (looked up before 2026-09-02). claim.forcedIntro says "this is a new
        // profile", which is false here, so the deletion intro shows instead.
        existingProfile: { type: Boolean, default: false },
        // Why the forced gate opened (Dashboard's mandatoryClaimReason), echoed
        // on every claim event so each gate's funnel can be read on its own —
        // `mandatory_claim_shown` alone says only how many saw it.
        reason: { type: String, default: '' },
    },
    data () {
        return {
            status: null,
            loaded: false,
            open: false,
            // Set only after a mail goes out, and only for this screen: the
            // server keeps no record of a pending confirmation, so a reload
            // legitimately drops back to the address step.
            sentTo: '',
            email: '',
            sending: false,
            error: '',
            // Set once a forced modal has been legitimately dismissed (only
            // possible once canClose is true), so it doesn't reopen itself on
            // the next reactive tick just because `forced` is still passed in.
            dismissed: false,
            // Steps already reported to GA for this instance, so a re-render or
            // a reactive tick can't inflate the funnel's top.
            reportedSteps: [],
        };
    },
    computed: {
        // Being signed in is not enough — it has to be this profile, otherwise
        // someone signed in as A would see their own claim state on B's page.
        ownsThisProfile () {
            return Boolean(
                this.status?.signedIn
                && this.profileSteamId
                && String(this.status.steamId) === String(this.profileSteamId)
            );
        },
        stage () {
            if (!this.loaded) {
                return 'loading';
            }

            // Already claimed: the card disappears entirely, for the owner as
            // much as for a visitor. There is nothing to do and nothing worth
            // saying — a permanent "claimed" badge is just clutter on a card
            // that exists to show whose profile this is.
            if (this.status?.profileClaimed) {
                return 'taken';
            }

            // Everything past step 1 requires having just come back from Steam.
            // A session cookie left over from an earlier visit never changes
            // what is shown: the user cannot see that cookie, so letting it
            // skip the sign-in step looked like the app demanding an e-mail out
            // of nowhere, and an abandoned attempt must start over anyway.
            if (this.justReturnedFromSteam) {
                if (!this.ownsThisProfile) {
                    return 'mismatch';
                }

                return this.sentTo ? 'pending' : 'need-email';
            }

            return 'signed-out';
        },
        visible () {
            return !['loading', 'taken'].includes(this.stage);
        },
        effectiveOpen () {
            return (this.forced && !this.dismissed) || this.open;
        },
        // Set by /claim/status when the signed-in visitor's own profile cannot
        // be registered — a hidden profile or a hidden library. Nothing they can
        // type will change it, so the form is withheld rather than shown and
        // then rejected.
        notRegisterable () {
            return this.status?.registerable === false;
        },
        // Games listed, every playtime zero — a different Steam setting from
        // a hidden library, so a different instruction.
        playtimeHidden () {
            return this.status?.reason === 'playtime_private';
        },
        canClose () {
            return !this.forced
                || this.dismissible
                || ['pending', 'taken', 'mismatch'].includes(this.stage)
                // A hidden library is a dead end too: the fix is in Steam's
                // settings, not in this modal, so holding the visitor here
                // only stops them from going to make it.
                || this.notRegisterable;
        },
        // Set by the api's redirect after a successful Steam round-trip. Paired
        // with the cookie check so a hand-typed ?signed_in=1 proves nothing.
        justReturnedFromSteam () {
            return this.$route?.query?.signed_in === '1' && Boolean(this.status?.signedIn);
        },
    },
    watch: {
        // The funnel's denominator. A step counts as seen only while the modal
        // is actually open — `stage` alone changes behind a closed modal (the
        // status call resolving is enough), which would report impressions
        // nobody had.
        effectiveOpen: { handler: 'reportStepShown', immediate: true },
        stage: 'reportStepShown',
    },
    mounted () {
        this.refresh();
        window.addEventListener('keydown', this.onKeydown);
    },
    beforeUnmount () {
        window.removeEventListener('keydown', this.onKeydown);
    },
    methods: {
        // Every claim event goes through here so `reason` is never forgotten on
        // one of them. Left out entirely on the optional card, not sent empty.
        track (name, params) {
            trackEvent(name, this.reason ? { ...params, reason: this.reason } : params);
        },
        // One `claim_step_shown` per stage this instance actually displays.
        // `forced` separates the mandatory gate from the optional card, and
        // `placement` separates the two optional mount points, so a drop-off
        // can be attributed without guessing which of the three cards it was.
        reportStepShown () {
            if (!this.effectiveOpen || ['loading', 'taken'].includes(this.stage)) {
                return;
            }

            if (this.reportedSteps.includes(this.stage)) {
                return;
            }

            this.reportedSteps.push(this.stage);
            this.track('claim_step_shown', {
                step: this.stage,
                forced: this.forced,
                placement: this.variant,
            });
        },
        onTrigger () {
            this.open = true;
            this.track('claim_button_clicked', { placement: this.variant });
        },
        goToOwnProfile () {
            // Leaving for their own profile is not a refusal; no dismiss event.
            this.hide();
            this.$router.push(`/user/${this.status.steamId}`);
        },
        onKeydown (event) {
            if (event.key === 'Escape' && this.effectiveOpen) {
                this.close();
            }
        },
        close () {
            if (!this.canClose) {
                // Backdrop clicks/Escape reach here even with the X hidden;
                // ignore them while the mandatory-claim gate is still active.
                return;
            }

            // The one exit nothing else records: on a closable gate, how many
            // walk away at each step is the question the gate exists to answer.
            if (this.effectiveOpen) {
                this.track('claim_dismissed', {
                    step: this.stage,
                    forced: this.forced,
                    placement: this.variant,
                });
            }

            this.hide();
        },
        hide () {
            if (this.forced) {
                this.dismissed = true;
            }

            this.open = false;
            this.error = '';
        },
        async refresh () {
            try {
                const { data } = await claimAPI.getStatus(this.profileSteamId);

                this.status = data;
            } catch (err) {
                if (this.forced) {
                    // Faking "already claimed" here would silently defeat the
                    // whole point of forced mode — a flaky mobile connection
                    // (this call is a cross-subdomain fetch to api.*, exactly
                    // what drops first on a bad signal) would let the visitor
                    // straight through with nothing claimed. One retry after
                    // a beat covers a transient blip; if it still fails, fall
                    // back to "not signed in" so the gate stays up and the
                    // Steam button is still there to retry by hand — never to
                    // "taken", which would hide it.
                    if (!this._refreshRetried) {
                        this._refreshRetried = true;
                        setTimeout(() => { this.refresh(); }, 1500);

                        return;
                    }

                    console.warn('[claim] status check failed twice in forced mode:', err?.message || err);
                    this.status = { signedIn: false, profileClaimed: false };
                } else {
                    // Claim is an enhancement, never the reason a dashboard fails
                    // to render. Treat an unreachable status endpoint as "profile
                    // already claimed" so the failure hides the UI rather than
                    // offering a button that cannot work.
                    this.status = { signedIn: false, profileClaimed: true };
                }
            } finally {
                this.loaded = true;

                // Steam sends the user back here mid-flow, so pick the modal up
                // where it left off rather than making them hunt for the button
                // again. Only on the profile that is actually theirs.
                // Includes the mismatch case: an error the user needs to read
                // is worth opening on its own, not hiding behind the button.
                // Not for an optional card while the forced gate is up: the gate
                // already carries the flow, and a second modal underneath it
                // reappears the moment the gate is closed.
                if (this.justReturnedFromSteam && (this.forced || !this.isClaimGateOpen())) {
                    this.open = true;
                }
            }
        },
        startLogin () {
            // Fired before the redirect: the Steam round-trip leaves the SPA,
            // so this is the last chance to record that step 1 converted. The
            // gap between this and `claim_step_shown{step:'need-email'}` is the
            // Steam-side drop-off, which nothing else can see.
            this.track('claim_steam_signin_clicked', { forced: this.forced });
            claimAPI.startSteamLogin(this.profileSteamId, this.lookupKey);
        },
        async submitEmail () {
            if (this.sending) {
                return;
            }

            this.sending = true;
            this.error = '';

            try {
                await claimAPI.submitEmail(this.email);
                // Reported here rather than in the parent: only the forced
                // instance has a listener for `email-submitted`, so a parent-side
                // event would fire for variant B only and leave the A arm with
                // no conversion at all. `resend` deliberately reports too — a
                // resend is a real send, and the two are told apart by `resent`.
                this.track('claim_email_submitted', {
                    forced: this.forced,
                    placement: this.variant,
                    resent: Boolean(this.sentTo),
                });
                this.sentTo = this.email;
                this.email = '';
                this.$emit('email-submitted');
            } catch (err) {
                // The API's own text is English; these two refusals have a
                // translated equivalent and are the ones a real user hits.
                const code = err?.response?.data?.code;

                if (code === 'playtime_private') {
                    this.error = this.$t('claim.playtimePrivate');
                } else if (['profile_private', 'games_private'].includes(code)) {
                    this.error = this.$t('claim.notPublic');
                } else {
                    this.error = err?.response?.data?.error || this.$t('claim.mailError');
                }
            } finally {
                this.sending = false;
            }
        },
        async resend () {
            this.email = this.sentTo;
            await this.submitEmail();
        },
    },
};
</script>

<style scoped>
.claim--full {
  margin-top: 18px;
}

.claim--achievements {
  margin-top: 4px;
}

.claim--compact {
  margin-top: 8px;
  /* The compact variant lives inside the half-width profile card. Without
     min-width:0 the nowrap button below sets the flex item's automatic minimum,
     which is how it came to hang ~60px over the neighbouring card on a phone. */
  min-width: 0;
  width: 100%;
}

/* Fit the card rather than the text: at 360px the card is ~152px and the button
   measured 148px on one line, so it has to be allowed to wrap. */
.claim--compact .claim-btn {
  white-space: normal;
  width: 100%;
  padding: 9px 12px;
  font-size: 0.85rem;
  line-height: 1.3;
}

.claim-btn {
  background: var(--primary);
  color: #0b0e12;
  border: none;
  padding: 11px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s ease;
  white-space: nowrap;
}

.claim-btn:hover:not(:disabled) {
  background: var(--primary-hover);
}

/* Muted rather than the light grey used elsewhere in the app, which reads as a
   broken element against this dark panel. */
.claim-btn:disabled {
  background: #2a3f52;
  color: var(--muted);
  cursor: not-allowed;
}

/* The Valve image is the button, so the element itself carries no chrome. */
.claim-steam {
  display: block;
  margin: 4px auto 0;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 0;
  border-radius: 4px;
  transition: opacity 0.2s ease;
}

.claim-steam:hover {
  opacity: 0.85;
}

.claim-steam:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 3px;
}

.claim-btn--block {
  display: block;
  width: 100%;
  margin-top: 4px;
}

.claim--compact .claim-btn {
  padding: 7px 12px;
  font-size: 0.85rem;
}

.claim-btn-ico {
  margin-right: 6px;
}

.claim-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.66);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  /* Must sit above LoadingOverlay (z-index: 9999) — the mandatory-claim
     variant opens this modal while that overlay is showing "Syncing Your
     Library", and the claim prompt has to win that stack. */
  z-index: 10000;
}

.claim-modal {
  position: relative;
  width: 100%;
  max-width: 440px;
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 12px;
  padding: 28px;
  text-align: left;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.55);
}

.claim-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: var(--muted);
  font-size: 1rem;
  cursor: pointer;
  line-height: 1;
  padding: 4px;
}

.claim-close:hover {
  color: var(--text);
}

.claim-step {
  margin: 0 0 6px;
  color: var(--primary);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.09em;
}

.claim-heading {
  margin: 0 0 10px;
  font-size: 1.25rem;
  color: var(--text);
}

.claim-body {
  margin: 0 0 16px;
  color: var(--muted);
  line-height: 1.55;
}

.claim-steps {
  margin: 0 0 20px;
  padding-left: 20px;
  color: var(--muted);
  line-height: 1.6;
}

.claim-steps li + li {
  margin-top: 4px;
}

.claim-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.claim-input {
  width: 100%;
  padding: 11px 14px;
  border-radius: 8px;
  border: 1px solid var(--panel-border);
  /* Matches the date-range select in DashboardSummarySection; there is no
     --bg token, inputs in this app hardcode this value. */
  background: #0f141a;
  color: var(--text);
}

.claim-input:focus {
  outline: none;
  border-color: var(--primary);
}

.claim-link {
  display: block;
  margin: 12px auto 0;
  background: none;
  border: none;
  color: var(--muted);
  cursor: pointer;
  padding: 0;
  font-size: 0.85rem;
  text-decoration: underline;
}

.claim-link:hover {
  color: var(--text);
}

.claim-note {
  margin: 14px 0 0;
  color: var(--muted);
  font-size: 0.82rem;
  line-height: 1.5;
}

.claim-note--error {
  color: #ff6b6b;
}

.claim-note--forced {
  margin: 0 0 16px;
  color: var(--primary);
  font-size: 0.85rem;
}
</style>
